import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import './Gate.css';
import { 
  ShieldIcon, 
  QrIcon, 
  SearchIcon, 
  LogIcon
} from '../components/Icons';

const STATUS_CONFIG = {
  VALID: { color: '#059669', bg: '#ecfdf5', border: '#10b981', icon: '✓', title: 'Access Granted', subtitle: 'Pass is valid for entry today.' },
  ALREADY_USED_TODAY: { color: '#d97706', bg: '#fffbeb', border: '#f59e0b', icon: '!', title: 'Used Today', subtitle: 'This pass was already used for today. Please use it again tomorrow.' },
  ALREADY_USED: { color: '#d97706', bg: '#fffbeb', border: '#f59e0b', icon: '!', title: 'Already Used', subtitle: 'This pass has already been scanned and is no longer valid.' },
  EXPIRED: { color: '#dc2626', bg: '#fee2e2', border: '#fca5a5', icon: '×', title: 'Pass Expired', subtitle: 'This pass is no longer valid.' },
  REVOKED: { color: '#dc2626', bg: '#fee2e2', border: '#fca5a5', icon: '!', title: 'Pass Revoked', subtitle: 'This pass has been cancelled.' },
  WRONG_DAY: { color: '#dc2626', bg: '#fee2e2', border: '#fca5a5', icon: '?', title: 'Wrong Day', subtitle: 'This pass is not valid for today.' },
  NO_PASS_ISSUED: { color: '#dc2626', bg: '#fee2e2', border: '#fca5a5', icon: '!', title: 'No Pass Issued', subtitle: 'This guest was registered without a pass (Online/Hybrid choice).' },
  NOT_FOUND: { color: '#dc2626', bg: '#fee2e2', border: '#fca5a5', icon: 'NF', title: 'Invalid code', subtitle: 'No pass found with this code.' },
};

export default function Gate({ defaultTab = 'scan' }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const inputRef = useRef();
  const [scanning, setScanning] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [allLogs, setAllLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsSearch, setLogsSearch] = useState('');

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleVerify = async (manualCode) => {
    const val = (typeof manualCode === 'string') ? manualCode : code;
    const trimmed = val.trim().toUpperCase();
    if (!trimmed) return;

    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/passes/verify', { code: trimmed });
      const { valid, reason, pass, entryTime } = res.data;
      
      const config = STATUS_CONFIG[reason] || STATUS_CONFIG.NOT_FOUND;
      const resultObj = {
        ...config,
        pass,
        entryTime,
        code: trimmed,
        time: new Date(),
        reason
      };

      setResult(resultObj);
      setHistory(h => [resultObj, ...h.slice(0, 9)]);
      setCode('');
      if (valid) {
        // Trigger a refresh of logs if we were on that tab (unlikely but good for consistency)
        if (activeTab === 'logs') fetchLogs();
      }
    } catch (err) {
      setResult({
        ...STATUS_CONFIG.NOT_FOUND,
        subtitle: err.response?.data?.message || 'Verification failed',
        code: trimmed,
        time: new Date()
      });
    } finally {
      setLoading(false);
      setScanning(false);
    }
  };

  const reset = () => {
    setResult(null);
    setCode('');
    inputRef.current?.focus();
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') handleVerify();
  };

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await api.get('/passes/entry-logs');
      if (res.data.success) {
        setAllLogs(res.data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const filteredLogs = allLogs.filter(log => {
    const s = logsSearch.toLowerCase();
    return (
      log.guestName.toLowerCase().includes(s) ||
      log.passCode.toLowerCase().includes(s) ||
      log.event?.title?.toLowerCase().includes(s) ||
      log.issuedBy?.name?.toLowerCase().includes(s)
    );
  });

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab, fetchLogs]);

  if (activeTab === 'logs') {
    return (
      <div className="page-shell">
        <div className="page-header">
          <div>
            <div className="page-title">Entry Logs</div>
            <div className="page-subtitle">Track and monitor visitor entries in real-time</div>
          </div>
          <div className="page-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ color: '#64748b', fontSize: '0.875rem', fontWeight: 600 }}>
              {filteredLogs.length} Records found
            </div>
            <button className="btn btn-ghost btn-sm" onClick={fetchLogs}>Refresh</button>
          </div>
        </div>

        <div className="page-body">
          {/* Search Toolbar */}
          <div className="card toolbar" style={{ marginBottom: '1.25rem' }}>
            <input
              className="toolbar-input"
              placeholder="Search by guest name, pass code, or event title..."
              value={logsSearch}
              onChange={(e) => setLogsSearch(e.target.value)}
            />
          </div>
          
          {logsLoading ? (
            <div style={{ padding: '4rem', textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto' }} />
              <p style={{ marginTop: '1rem', color: 'var(--text-3)', fontSize: '0.875rem' }}>Loading entry records...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '3rem 2rem', textAlign: 'center', color: 'var(--text-3)' }}>
                <div style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                  {logsSearch ? 'No matching records' : 'No entries recorded'}
                </div>
                <div style={{ fontSize: '0.9375rem', marginTop: '0.5rem' }}>
                  {logsSearch ? 'Try adjusting your search terms' : 'No visitor check-ins have been recorded yet.'}
                </div>
              </div>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {filteredLogs.map((log, idx) => (
                <div key={log._id} style={{ 
                  padding: '1.25rem 1.5rem', 
                  borderBottom: idx < filteredLogs.length - 1 ? '1px solid var(--border-1)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '2rem'
                }}>
                  <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', flex: 1 }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#f8fafc', color: '#475569', border: '1px solid var(--border-1)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <div style={{ fontSize: '18px', fontWeight: 800 }}>{new Date(log.entryTime).getDate()}</div>
                      <div style={{ fontSize: '10px', fontWeight: 700 }}>{new Date(log.entryTime).toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</div>
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '1.0625rem', color: 'var(--text-1)' }}>{log.guestName}</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-3)', display: 'flex', gap: '0.75rem', marginTop: '0.25rem' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--navy)', fontWeight: 600 }}>{log.passCode}</span>
                        <span className="dot">·</span>
                        <span>{log.event?.title || 'General Entry'}</span>
                        <span className="dot">·</span>
                        <span>{log.event?.venue || 'Campus'}</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-1)' }}>
                      {new Date(log.entryTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: '0.25rem' }}>
                      <div style={{ fontWeight: 600 }}>Host: <span style={{ color: 'var(--text-1)' }}>{log.issuedBy?.name}</span></div>
                      <div style={{ marginTop: '0.125rem' }}>Verified By: <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{log.verifiedBy?.name || 'Gate System'}</span></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="gate-page">
      <div className="gate-left">
        <div className="gate-shell">
          <div className="gate-header">
            <div className="gate-logo"><ShieldIcon /> Gate security</div>
          </div>

          <h1 className="gate-title">Scan Entry</h1>
          <p className="gate-subtitle">Scan a QR code or enter the printed pass code to verify a visitor.</p>

          <div className={`scan-area ${scanning ? 'scanning' : ''}`}>
            {scanning ? (
              <div className="scan-animation">
                <div className="scan-frame">
                  <div className="scan-line" />
                  <div className="scan-corner tl" /><div className="scan-corner tr" />
                  <div className="scan-corner bl" /><div className="scan-corner br" />
                </div>
                <div className="scan-text">Align QR code within the frame</div>
                <button className="btn btn-ghost" style={{ marginTop: '2rem', color: 'white' }} onClick={() => setScanning(false)}>
                  Cancel Scan
                </button>
              </div>
            ) : result ? (
              <div className="scan-result" style={{ borderColor: result.border }}>
                <div className="result-icon" style={{ color: result.color }}>{result.icon}</div>
                <div className="result-status" style={{ color: result.color }}>{result.title}</div>
                <div className="result-subtitle">{result.subtitle}</div>
                {result.pass && (
                  <div className="result-pass-info">
                    <div className="pass-info-name">{result.pass.guestName}</div>
                    <div className="pass-info-detail">
                      {result.pass.profession}{result.pass.organization ? ` · ${result.pass.organization}` : ''}
                    </div>
                    <div className="pass-info-event">{result.pass.event?.title}</div>
                    <div className="pass-info-event">
                      {result.pass.event?.venue}
                      {result.pass.event?.date ? ` · ${new Date(result.pass.event.date).toLocaleDateString('en-IN')}` : ''}
                    </div>
                    <div className="pass-info-host">
                      Host: {result.pass.issuedBy?.name} ({result.pass.issuedBy?.department})
                    </div>
                    {result.entryTime && (
                      <div className="previous-entry-banner">
                        Previous entry: {new Date(result.entryTime).toLocaleString('en-IN')}
                      </div>
                    )}
                  </div>
                )}
                <button type="button" className="btn-verify-again" onClick={reset}>Verify another</button>
              </div>
            ) : (
              <div className="scan-idle">
                <div className="qr-icon">
                  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" aria-hidden>
                    <rect x="4" y="4" width="28" height="28" rx="4" stroke="var(--navy-deep)" strokeWidth="3" fill="none" />
                    <rect x="12" y="12" width="12" height="12" rx="2" fill="var(--navy)" />
                    <rect x="48" y="4" width="28" height="28" rx="4" stroke="var(--navy-deep)" strokeWidth="3" fill="none" />
                    <rect x="56" y="12" width="12" height="12" rx="2" fill="var(--navy)" />
                    <rect x="4" y="48" width="28" height="28" rx="4" stroke="var(--navy-deep)" strokeWidth="3" fill="none" />
                    <rect x="12" y="56" width="12" height="12" rx="2" fill="var(--navy)" />
                    <rect x="48" y="48" width="8" height="8" rx="1" fill="var(--red)" />
                    <rect x="62" y="48" width="8" height="8" rx="1" fill="var(--red)" />
                    <rect x="48" y="62" width="8" height="8" rx="1" fill="var(--red)" />
                    <rect x="62" y="62" width="8" height="8" rx="1" fill="var(--red)" />
                  </svg>
                </div>
                <p className="scan-hint">Use a QR scanner or enter the code below.</p>
              </div>
            )}
          </div>

          <div className="gate-divider"><span>OR</span></div>

          <div className="code-input-section">
            <div className="code-input-row">
              <input 
                ref={inputRef}
                className="code-input" 
                placeholder="VP-XXXXXXXX" 
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                maxLength={15}
              />
              <button className="verify-btn" onClick={handleVerify} disabled={loading || !code.trim()}>
                {loading ? <div className="spinner" style={{ width: 20, height: 20, borderColor: 'white' }} /> : 'Go'}
              </button>
            </div>
            <p className="code-hint" style={{ marginTop: '0.625rem', color: 'var(--text-3)', fontSize: '0.8125rem' }}>Press Enter or tap Go.</p>
          </div>
        </div>
      </div>

      <div className="gate-right">
        <div className="gate-right-header">
          <h2 className="gate-history-title">Recent verifications</h2>
          <p className="gate-history-sub">Latest checks in this session</p>
        </div>

        {history.length === 0 ? (
          <div className="empty-state" style={{ padding: '2.5rem 1.25rem' }}>
            <div className="empty-icon">Quiet gate</div>
            <div className="empty-text">No scans yet.</div>
          </div>
        ) : (
          <div className="history-list">
            {history.map((h, i) => (
              <div key={i} className="history-item fade-in" style={{ borderLeft: `4px solid ${h.border || h.color}` }}>
                <div className="history-item-title-row">
                  <div>
                    <div className="history-item-meta">
                      <span style={{ fontSize: '0.9375rem' }}>{h.icon}</span>
                      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--text-sm)', color: h.color }}>
                        {h.title}
                      </span>
                    </div>
                    {h.pass && <div className="history-guest-name">{h.pass.guestName}</div>}
                    <code className="history-item-code">{h.code}</code>
                  </div>
                  <div className="history-item-time">{h.time?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                {h.pass && (
                  <div className="history-item-extra">{h.pass.profession} · {h.pass.event?.title}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {history.length > 0 && (
          <div className="gate-stats">
            {[
              { label: 'Granted', value: history.filter(h => h.reason === 'VALID').length, color: '#059669' },
              { label: 'Rejected', value: history.filter(h => ['EXPIRED', 'REVOKED', 'NOT_FOUND'].includes(h.reason)).length, color: '#dc2626' },
              { label: 'Duplicates', value: history.filter(h => ['ALREADY_USED', 'ALREADY_USED_TODAY'].includes(h.reason)).length, color: '#d97706' },
            ].map(s => (
              <div key={s.label} className="gate-stat">
                <div className="gate-stat-value" style={{ color: s.color }}>{s.value}</div>
                <div className="gate-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
